import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  CContainer, CRow, CCol, CCard, CCardBody, CButton, CSpinner, CFormInput, CBadge 
} from '@coreui/react';
import { ArrowLeft, ShieldCheck, Tag, CreditCard, BookOpen } from 'lucide-react';
import api from '../../api/axiosInstance';
import Swal from 'sweetalert2';

// Payment Icons SVG Components
const GPayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#F8F9FA"/>
    <path d="M15.4 12.18c0-.44-.04-.86-.11-1.27H12v2.41h1.91c-.08.45-.34.84-.73 1.1l1.18.92c.69-.64 1.09-1.58 1.09-2.66z" fill="#4285F4"/>
    <path d="M12 15.6c.97 0 1.79-.32 2.39-.87l-1.18-.92c-.32.22-.74.35-1.21.35-.93 0-1.72-.63-2.01-1.48l-1.2.93c.6 1.18 1.83 1.99 3.21 1.99z" fill="#34A853"/>
    <path d="M9.99 12.68c-.08-.22-.12-.46-.12-.71 0-.25.04-.49.12-.71l-1.2-.93c-.25.49-.39 1.04-.39 1.64 0 .6.14 1.15.39 1.64l1.2-.93z" fill="#FBBC05"/>
    <path d="M12 9.2c.53 0 1.01.18 1.38.54l1.04-1.04C13.79 8.12 12.97 7.8 12 7.8c-1.38 0-2.61.81-3.21 1.99l1.2.93c.29-.85 1.08-1.48 2.01-1.48z" fill="#EA4335"/>
  </svg>
);

const PhonePeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#5F259F"/>
    <path d="M16.5 7.5H13.2V9h1.8c.8 0 1.5.7 1.5 1.5v3.6c0 .8-.7 1.5-1.5 1.5h-1.8v1.8h-1.8V7.5H7.5V9h2.4v7.5H11.7v-1.8h3.3c1.8 0 3.3-1.5 3.3-3.3V10.8c0-1.8-1.5-3.3-3.3-3.3z" fill="#FFFFFF"/>
  </svg>
);

const PlansCheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const targetCategoryId = queryParams.get('categoryId');
  const targetCategoryName = queryParams.get('categoryName');

  const [tiers, setTiers] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  const memberId = localStorage.getItem('ID') || localStorage.getItem('memberId');

  // Page open hote hi hamesha top par scroll karein
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, targetCategoryId, targetCategoryName]);

  const getTierName = (tier) => {
    return tier?.tierName || tier?.TierName || tier?.sectionName || tier?.SectionName || 'Standard Plan';
  };

  const getValidityDisplay = (tier) => {
    const expiry = tier?.expiryDate || tier?.ExpiryDate;
    if (expiry) {
      const formattedDate = new Date(expiry).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
      return `Valid till ${formattedDate}`;
    }
    const duration = tier?.durationInDays ?? tier?.DurationInDays;
    if (duration !== null && duration > 0) return `Valid for ${duration} Days`;
    return 'Lifetime Validity';
  };

  const renderCategoriesDisplay = (tier) => {
    const tName = getTierName(tier).toLowerCase();
    
    if (tName.includes('sigma')) {
      return (
        <div className="mt-1.5">
          <CBadge color="warning" className="px-2 py-0.5 fw-bold text-dark" style={{ fontSize: '11px' }}>
            🌟 All Access
          </CBadge>
        </div>
      );
    }

    const titles = tier.categoryTitles || tier.CategoryTitles;

    if (Array.isArray(titles) && titles.length > 0) {
      return (
        <div className="d-flex flex-wrap gap-1 align-items-center mt-1.5">
          {titles.map((cat, idx) => (
            <CBadge key={idx} color="info" variant="outline" className="px-2 py-0.5 fw-medium" style={{ fontSize: '11px' }}>
              {typeof cat === 'object' ? cat.name || cat.categoryName : cat}
            </CBadge>
          ))}
        </div>
      );
    }

    if (typeof titles === 'string' && titles.trim() !== '') {
      return (
        <div className="d-flex align-items-center gap-1 mt-1.5">
          <BookOpen size={12} className="text-primary flex-shrink-0" />
          <span className="text-secondary fw-semibold" style={{ fontSize: '11.5px', lineHeight: '1.2' }}>
            {titles}
          </span>
        </div>
      );
    }
    return null;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/membershiptiers/GetAllTiers');
      const allTiers = res.data || [];

      if (!targetCategoryId && !targetCategoryName) {
        setTiers(allTiers);
        if (allTiers.length > 0) {
          setSelectedPlan(allTiers[0]);
        }
        setLoading(false);
        return;
      }

      const cleanTargetName = (targetCategoryName || '').toLowerCase().trim();

      const filtered = allTiers.filter((t) => {
        const tName = getTierName(t).toLowerCase();
        
        if (tName.includes('sigma')) return true;

        const tCategoryIds = String(t.categoryIds || t.CategoryIds || '')
          .split(',')
          .map(id => id.trim());
        const matchesId = targetCategoryId && tCategoryIds.includes(String(targetCategoryId).trim());
        if (matchesId) return true;

        const tCategoryTitles = String(t.categoryTitles || t.CategoryTitles || '').toLowerCase();
        
        if (cleanTargetName) {
          const isDirectSection = ['beeta', 'beta', 'gamma', 'delta', 'alpha'].includes(cleanTargetName);
          
          if (isDirectSection) {
            return tName.includes(cleanTargetName);
          }

          if (cleanTargetName.includes('nmtc')) {
            return tCategoryTitles.includes('nmtc') || tName.includes('nmtc');
          }

          return tCategoryTitles.includes(cleanTargetName) || tName.includes(cleanTargetName);
        }

        return false;
      });

      const finalTiers = filtered.length > 0 ? filtered : allTiers;
      setTiers(finalTiers);

      if (finalTiers.length > 0) {
        let matched = null;
        if (cleanTargetName) {
          matched = finalTiers.find((t) => getTierName(t).toLowerCase().includes(cleanTargetName));
        }
        setSelectedPlan(matched || finalTiers[0]);
      }
    } catch (err) {
      console.error("Error loading plans:", err);
      Swal.fire({
        icon: 'error',
        title: 'Error loading plans',
        showConfirmButton: false,
        timer: 1500
      });
    } finally {
      setLoading(false);
    }
  }, [targetCategoryId, targetCategoryName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyCoupon = () => {
    if (couponCode.trim().toUpperCase() === 'AMBI200') {
      setDiscount(200);
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Coupon Code',
        text: 'Try "AMBI200"',
        showConfirmButton: false,
        timer: 1500
      });
    }
  };

  const handleBuyNow = async () => {
    if (!memberId) {
      Swal.fire({
        icon: 'error',
        title: 'Please log in to continue',
        showConfirmButton: false,
        timer: 1500
      });
      navigate('/login', { state: { from: location.pathname + location.search } });
      return;
    }

    if (!selectedPlan) return;

    try {
      setPurchasing(true);
      const targetTierId = selectedPlan.tierId || selectedPlan.TierId || selectedPlan.id;
      const rawPrice = selectedPlan.price || selectedPlan.Price || 0;
      const finalPrice = Math.max(0, rawPrice - discount);

      await api.post(`/api/members/${memberId}/upgrade`, {
        tierId: targetTierId,
        amount: finalPrice
      });

      Swal.fire({
        icon: 'success',
        title: 'Plan activated successfully!',
        showConfirmButton: false,
        timer: 1500
      });
      navigate('/profile');
    } catch (err) {
      console.error("Payment failed", err);
      Swal.fire({
        icon: 'error',
        title: 'Failed to complete purchase.',
        showConfirmButton: false,
        timer: 1500
      });
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5 my-5">
        <CSpinner color="primary" size="lg" />
        <p className="mt-2 fw-semibold text-muted">Loading Subscription Checkout...</p>
      </div>
    );
  }

  const rawPrice = selectedPlan?.price ?? selectedPlan?.Price ?? 0;
  const finalPayable = Math.max(0, rawPrice - discount);
  const displayTitleName = targetCategoryName ? targetCategoryName.toUpperCase() : 'ALL MEMBERSHIP PLANS';

  return (
    <div className="bg-light py-4 pb-5 min-vh-100" style={{ paddingBottom: '90px' }}>
      <CContainer style={{ maxWidth: '1100px' }}>
        
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h3 className="fw-bold text-dark mb-1">
              AMBIPIE Package for {displayTitleName}
            </h3>
            <span className="text-muted small d-flex align-items-center gap-1">
              <ShieldCheck size={16} className="text-primary" /> Verified Academic Syllabus Coverage
            </span>
          </div>
          <CButton 
            color="outline-secondary" 
            size="sm" 
            className="fw-bold d-flex align-items-center gap-1"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={14} /> Back
          </CButton>
        </div>

        <CRow className="g-4">
          
          {/* Left Column */}
          <CCol lg={7}>
            <div className="d-flex flex-column gap-3">
              {tiers.length > 0 ? (
                tiers.map((t) => {
                  const tId = t.tierId || t.TierId || t.id;
                  const selectedId = selectedPlan?.id || selectedPlan?.tierId || selectedPlan?.TierId;
                  const isSelected = selectedId === tId;
                  const tPrice = t.price ?? t.Price ?? 0;
                  const origPrice = tPrice > 0 ? Math.round(tPrice * 1.5) : 0;
                  const isBestValue = getTierName(t).toLowerCase().includes('sigma');

                  return (
                    <div key={tId} className="position-relative">
                      {isBestValue && (
                        <span 
                          className="badge bg-primary text-white position-absolute fw-bold px-2 py-1"
                          style={{ top: '-10px', left: '20px', zIndex: 2, fontSize: '10px', letterSpacing: '0.5px' }}
                        >
                          BEST VALUE
                        </span>
                      )}

                      <div 
                        onClick={() => setSelectedPlan(t)}
                        className="p-3 bg-white border rounded-3 cursor-pointer d-flex align-items-center justify-content-between transition-all"
                        style={{
                          borderColor: isSelected ? '#0d6efd' : '#dee2e6',
                          borderWidth: isSelected ? '2px' : '1px',
                          boxShadow: isSelected ? '0 4px 12px rgba(13, 110, 253, 0.12)' : 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <div className="d-flex align-items-start gap-3">
                          <input 
                            type="radio" 
                            name="planSelection" 
                            checked={isSelected}
                            onChange={() => setSelectedPlan(t)}
                            className="mt-1"
                            style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
                          />
                          <div>
                            <h6 className="fw-bold text-dark mb-0 fs-6">{getTierName(t)}</h6>
                            <span className="text-muted small d-block">{getValidityDisplay(t)}</span>
                            
                            {renderCategoriesDisplay(t)}
                          </div>
                        </div>

                        <div className="text-end flex-shrink-0 ms-2">
                          <div className="d-flex align-items-center gap-2 justify-content-end">
                            <span className="fw-bold text-dark fs-5">₹{tPrice}</span>
                            {origPrice > 0 && (
                              <span className="text-muted text-decoration-line-through small">₹{origPrice}</span>
                            )}
                          </div>
                          {tPrice > 0 && (
                            <CBadge color="success" className="fw-normal px-2 py-0.5" style={{ fontSize: '10px' }}>
                              SAVE 33%
                            </CBadge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="alert alert-warning text-center">No plans found.</div>
              )}
            </div>
          </CCol>

          {/* Right Column */}
          <CCol lg={5}>
            
            {/* Coupon Card */}
            <CCard className="border-0 shadow-sm rounded-3 mb-3">
              <CCardBody className="p-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Tag size={16} className="text-primary" />
                  <span className="fw-bold text-dark small">Have a Coupon Code?</span>
                </div>
                <div className="d-flex gap-2">
                  <CFormInput 
                    placeholder="Enter Coupon (e.g. AMBI200)" 
                    size="sm"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  <CButton color="dark" size="sm" className="px-3 fw-bold" onClick={handleApplyCoupon}>
                    Apply
                  </CButton>
                </div>
                {discount > 0 && (
                  <span className="text-success small fw-bold d-block mt-1">
                    ✓ ₹{discount} Coupon Discount Applied!
                  </span>
                )}
              </CCardBody>
            </CCard>

            {/* Price Summary */}
            <CCard className="border-0 shadow-sm rounded-3 mb-3">
              <CCardBody className="p-3">
                <h6 className="fw-bold text-dark border-bottom pb-2 mb-3">Price Summary</h6>
                
                <div className="d-flex justify-content-between text-muted small mb-2">
                  <span>Selected Package Price ({selectedPlan ? getTierName(selectedPlan) : ''})</span>
                  <span className="fw-semibold text-dark">₹{rawPrice}</span>
                </div>

                {discount > 0 && (
                  <div className="d-flex justify-content-between text-success small mb-2">
                    <span>Coupon Discount</span>
                    <span className="fw-semibold">- ₹{discount}</span>
                  </div>
                )}

                <div className="d-flex justify-content-between text-muted small mb-3">
                  <span>Estimated Taxes</span>
                  <span className="fw-semibold text-dark">₹0</span>
                </div>

                <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                  <span className="fw-bold text-dark fs-6">Total Amount</span>
                  <span className="fw-bold text-primary fs-4">₹{finalPayable}</span>
                </div>
              </CCardBody>
            </CCard>

            {/* Payment Options */}
            <CCard className="border-0 shadow-sm rounded-3 p-3">
              <span className="fw-bold text-dark small mb-2 d-block">Pay with:</span>
              <div className="d-flex gap-2 mb-3">
                <div className="border rounded p-2 text-center flex-fill bg-white d-flex flex-column align-items-center justify-content-center gap-1 shadow-2xs">
                  <GPayIcon />
                  <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>GPay</span>
                </div>
                <div className="border rounded p-2 text-center flex-fill bg-white d-flex flex-column align-items-center justify-content-center gap-1 shadow-2xs">
                  <PhonePeIcon />
                  <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>PhonePe</span>
                </div>
                <div className="border rounded p-2 text-center flex-fill bg-white d-flex flex-column align-items-center justify-content-center gap-1 shadow-2xs">
                  <CreditCard size={22} className="text-primary" />
                  <span className="fw-bold text-dark" style={{ fontSize: '11px' }}>Other</span>
                </div>
              </div>

              <CButton 
                color="dark" 
                className="w-100 py-2.5 fw-bold text-white fs-6 shadow-sm rounded-2"
                onClick={handleBuyNow}
                disabled={purchasing || !selectedPlan}
              >
                {purchasing ? <CSpinner size="sm" /> : `Buy Now →`}
              </CButton>
            </CCard>

          </CCol>

        </CRow>

      </CContainer>

      {/* STICKY FOOTER BAR */}
      {selectedPlan && (
        <div 
          className="position-fixed bottom-0 start-0 w-100 text-white py-2.5 px-3 px-md-4 shadow-lg d-flex align-items-center justify-content-between z-3"
          style={{ backgroundColor: '#0B1E36', borderTop: '2px solid #F59E0B' }}
        >
          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold text-light d-none d-sm-inline" style={{ fontSize: '14px' }}>
              AMBIPIE for <strong className="text-white">{getTierName(selectedPlan)}</strong>
            </span>
            <span className="fw-bold text-light d-inline d-sm-none" style={{ fontSize: '13px' }}>
              {getTierName(selectedPlan)}
            </span>
          </div>

          <div className="d-flex align-items-center gap-3">
            <div className="text-end">
              <div className="fw-bold text-warning fs-5">₹{finalPayable}</div>
              <small className="text-light opacity-75 d-none d-md-block" style={{ fontSize: '10px' }}>
                for {getTierName(selectedPlan)}
              </small>
            </div>

            <button 
              className="btn btn-light text-dark fw-bold px-3 py-2 rounded-2 shadow-sm d-flex align-items-center gap-1"
              style={{ fontSize: '13.5px' }}
              onClick={handleBuyNow}
              disabled={purchasing}
            >
              {purchasing ? <CSpinner size="sm" /> : <>Continue to Pay &rarr;</>}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default PlansCheckoutPage;